import { useCallback, useState } from 'react'
import { MAX_PAGE_DEPTH, getPageDepth } from '../utils/pageTreeUtils'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import { usePageMutations } from '@/lib/db/rxdb/hooks'
import {
  compareRanks,
  generateRankBetween,
} from '@/lib/utils/lexorank'
import { rebalanceSortOrders } from '@/lib/utils/sortOrder'

export type PageDropPosition = 'before' | 'after' | 'child'

export type PageReorderEvent = {
  sourceId: string
  targetId: string
  dropPosition: PageDropPosition
}

/**
 * Check if targetId is a descendant of ancestorId in the page tree.
 * Used to prevent circular references when reparenting.
 */
function isDescendant(
  pages: Array<PageDocument>,
  ancestorId: string,
  targetId: string,
): boolean {
  let current = pages.find((p) => p.id === targetId)
  while (current) {
    if (current.parentPageId === ancestorId) return true
    if (!current.parentPageId) return false
    current = pages.find((p) => p.id === current!.parentPageId)
  }
  return false
}

/**
 * Get the max depth of a page's subtree (how many levels deep its descendants go).
 */
function getSubtreeDepth(pages: Array<PageDocument>, pageId: string): number {
  const children = pages.filter((p) => p.parentPageId === pageId && !p.isDeleted)
  if (children.length === 0) return 0
  return 1 + Math.max(...children.map((c) => getSubtreeDepth(pages, c.id)))
}

function getSortedSiblings(
  pages: Array<PageDocument>,
  parentId: string | null,
): Array<PageDocument> {
  return pages
    .filter((p) => (p.parentPageId || null) === parentId && !p.isDeleted)
    .sort((a, b) => {
      if (a.sortOrder && b.sortOrder) return a.sortOrder.localeCompare(b.sortOrder)
      return a.createdOn - b.createdOn
    })
}

export function usePageReorder(pages: Array<PageDocument>) {
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [dropIndicator, setDropIndicator] = useState<{
    id: string
    position: PageDropPosition
  } | null>(null)

  const { updatePage } = usePageMutations()

  const handleReorder = useCallback(
    (event: PageReorderEvent) => {
      const source = pages.find((p) => p.id === event.sourceId)
      const target = pages.find((p) => p.id === event.targetId)
      if (!source || !target) return

      // Prevent dropping on self
      if (event.sourceId === event.targetId) return

      // Prevent circular references
      if (
        event.dropPosition === 'child' &&
        isDescendant(pages, event.sourceId, event.targetId)
      ) {
        return
      }

      // Prevent exceeding max nesting depth when reparenting
      if (event.dropPosition === 'child') {
        const targetDepth = getPageDepth(pages, event.targetId)
        const sourceSubtreeDepth = getSubtreeDepth(pages, event.sourceId)
        // target is at depth N, source becomes child at depth N+1,
        // source's deepest descendant would be at depth N+1+sourceSubtreeDepth
        if (targetDepth + 1 + sourceSubtreeDepth > MAX_PAGE_DEPTH) {
          return
        }
      }

      if (event.dropPosition === 'child') {
        // Reparent: make source a child of target (append at end)
        const targetChildren = getSortedSiblings(pages, event.targetId)
        const newChildren = [...targetChildren.filter((p) => p.id !== source.id), source]

        // If any child has null sortOrder, rebalance all children
        if (newChildren.some((item) => !item.sortOrder)) {
          void rebalanceSortOrders(
            newChildren,
            async (id, updates) => {
              if (id === source.id) {
                await updatePage(id, { ...updates, parentPageId: event.targetId })
              } else {
                await updatePage(id, updates)
              }
            },
          )
        } else {
          const lastSortOrder = targetChildren.at(-1)?.sortOrder ?? null
          const nextSortOrder = generateRankBetween(lastSortOrder, null)
          void updatePage(event.sourceId, {
            parentPageId: event.targetId,
            sortOrder: nextSortOrder,
          })
        }
        return
      }

      // before/after: reorder among siblings of the target's parent
      const newParentId = target.parentPageId || null

      // Prevent circular references for before/after too
      if (
        newParentId !== (source.parentPageId || null) &&
        isDescendant(pages, event.sourceId, event.targetId)
      ) {
        return
      }

      const siblings = getSortedSiblings(pages, newParentId)

      // Remove source from siblings list if it's currently a sibling
      const reordered = siblings.filter((p) => p.id !== event.sourceId)

      const targetIndex = reordered.findIndex((p) => p.id === event.targetId)
      if (targetIndex === -1) return

      const insertIndex =
        event.dropPosition === 'after' ? targetIndex + 1 : targetIndex

      reordered.splice(insertIndex, 0, source)

      const movedIndex = reordered.findIndex((p) => p.id === source.id)
      const beforeSort =
        movedIndex > 0 ? (reordered[movedIndex - 1]?.sortOrder ?? null) : null
      const afterSort =
        movedIndex < reordered.length - 1
          ? (reordered[movedIndex + 1]?.sortOrder ?? null)
          : null

      // Check if rebalancing is needed:
      // 1. Any sibling has null sortOrder (legacy pages created before ordering)
      // 2. Neighboring ranks are too close / inverted
      const hasNullSortOrders = reordered.some((item) => !item.sortOrder)
      const shouldRebalance =
        hasNullSortOrders ||
        (beforeSort !== null &&
          afterSort !== null &&
          compareRanks(beforeSort, afterSort) >= 0)

      if (shouldRebalance) {
        void rebalanceSortOrders(
          reordered,
          async (id, updates) => {
            // Also update parentPageId for the source if it changed
            if (id === source.id && newParentId !== (source.parentPageId || null)) {
              await updatePage(id, { ...updates, parentPageId: newParentId })
            } else {
              await updatePage(id, updates)
            }
          },
        )
        return
      }

      const nextSortOrder = generateRankBetween(beforeSort, afterSort)
      if (nextSortOrder === source.sortOrder && newParentId === (source.parentPageId || null)) {
        return
      }

      const updates: Record<string, unknown> = { sortOrder: nextSortOrder }
      if (newParentId !== (source.parentPageId || null)) {
        updates.parentPageId = newParentId
      }

      void updatePage(event.sourceId, updates)
    },
    [pages, updatePage],
  )

  return {
    handleReorder,
    draggingId,
    setDraggingId,
    dropIndicator,
    setDropIndicator,
  }
}
